import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import type { GamePinInput, GamePinView } from '@wehunt/ui'
import { useApiFetch } from '../../shared/api'
import {
  createHuntGamePin,
  deleteHuntGamePin,
  listHuntGamePins,
  updateHuntGamePin,
  type GamePinBody,
  type GamePinDto,
} from './api/gamePinsApi'

const gamePinsKey = (groupId: string | undefined, huntId: string | undefined) =>
  ['clubhouse', 'groups', groupId, 'hunts', huntId, 'game-pins'] as const

/** Map the wire pin (location as {latitude,longitude}) to the UI view shape
 * (location as the web {lat,lon} GeoPt). */
function dtoToView(dto: GamePinDto): GamePinView {
  return {
    id: dto.id,
    reporterId: dto.reporterId,
    shooterId: dto.shooterId ?? null,
    pinType: dto.pinType,
    games: dto.games.map((g) => ({
      name: g.name,
      gender: g.gender,
      ageCategory: g.ageCategory,
      gameType: g.gameType,
    })),
    location: { lat: dto.location.latitude, lon: dto.location.longitude },
    // Carried through so the report can order the game rows by time and stamp
    // each one; the DTO already provides it.
    date: dto.date ?? null,
    comment: dto.comment ?? null,
  }
}

/** Map the UI write payload back to the wire body. */
function inputToBody(input: GamePinInput): GamePinBody {
  return {
    pinType: input.pinType,
    games: input.games,
    location: { latitude: input.location.lat, longitude: input.location.lon },
    shooterId: input.shooterId ?? null,
    comment: input.comment ?? null,
  }
}

export interface HuntGamePins {
  pins: GamePinView[]
  canManageAll: boolean
}

export interface HuntGamePinsResult {
  data?: HuntGamePins
  isLoading: boolean
  /**
   * The fetch failed — as distinct from a hunt where no game was reported.
   *
   * Without this the two are the same answer to the page, which renders "no
   * downed game recorded yet" either way: a hunt whose pins could not be read
   * (a permission error, a hunt the API cannot resolve in the requested team, an
   * upstream outage) looked exactly like a hunt where nobody shot anything, with
   * nothing on screen to suggest otherwise. False while the query is disabled or
   * still loading, so it never accuses a fetch that has not happened.
   */
  loadFailed: boolean
}

export function useHuntGamePins(
  groupId: string | undefined,
  huntId: string | undefined,
  // Game pins are only shown (and only exist) for an ended hunt's report, so the
  // fetch is gated on `ended`; the hook is still called unconditionally at the
  // route (rules of hooks) — only the query is disabled otherwise.
  ended = true,
): HuntGamePinsResult {
  const call = useApiFetch()
  const { state } = useAuth()
  const isSignedIn = state.status === 'signed-in'
  const { data, isLoading, isError } = useQuery({
    queryKey: gamePinsKey(groupId, huntId),
    enabled: isSignedIn && !!groupId && !!huntId && ended,
    queryFn: async (): Promise<HuntGamePins> => {
      const res = await listHuntGamePins(call, groupId ?? '', huntId ?? '')
      return { pins: res.pins.map(dtoToView), canManageAll: res.canManageAll }
    },
  })
  return { data, isLoading, loadFailed: isError }
}

export function useCreateHuntGamePin(groupId: string | undefined, huntId: string | undefined) {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: GamePinInput) => createHuntGamePin(call, groupId ?? '', huntId ?? '', inputToBody(input)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: gamePinsKey(groupId, huntId) })
    },
    onError: (e) => {
      console.error('Failed to create hunt game pin', e)
    },
  })
}

export function useUpdateHuntGamePin(groupId: string | undefined, huntId: string | undefined) {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: GamePinInput }) =>
      updateHuntGamePin(call, groupId ?? '', huntId ?? '', id, inputToBody(input)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: gamePinsKey(groupId, huntId) })
    },
    onError: (e) => {
      console.error('Failed to update hunt game pin', e)
    },
  })
}

export function useDeleteHuntGamePin(groupId: string | undefined, huntId: string | undefined) {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteHuntGamePin(call, groupId ?? '', huntId ?? '', id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: gamePinsKey(groupId, huntId) })
    },
    onError: (e) => {
      console.error('Failed to delete hunt game pin', e)
    },
  })
}
